import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import { Field } from './Field';
import { controlClasses } from './styles';

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'aria-describedby' | 'aria-invalid' | 'className' | 'required'
>;

export interface InputProps extends NativeInputProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  id?: string;
  labelHidden?: boolean;
  /** Wrapper class. The control's own classes are owned by `controlClasses`. */
  className?: string;
  /** Rendered inside the control on the left, e.g. "+91" or "₹". */
  prefix?: string;
}

/**
 * A labelled text input. The label is not optional and cannot be replaced by a
 * placeholder — see Field.
 *
 * The ref lands on the <input>, which is what react-hook-form's `register` needs.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required = false, id, labelHidden = false, className, prefix, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      {...(id ? { id } : {})}
      labelHidden={labelHidden}
      {...(className ? { className } : {})}
    >
      {({ id: fieldId, describedBy, invalid }) =>
        prefix ? (
          <div
            className={controlClasses(
              invalid,
              'flex items-center gap-2 p-0 focus-within:ring-2 focus-within:ring-focus focus-within:ring-offset-2',
            )}
          >
            <span className="select-none pl-3 text-ink-muted" aria-hidden="true">
              {prefix}
            </span>
            <input
              {...rest}
              ref={ref}
              id={fieldId}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              required={required}
              // The wrapper owns the ring; the inner input must not draw a second one.
              className="min-h-touch w-full flex-1 rounded-r-control border-0 bg-transparent pr-3 text-base text-ink placeholder:text-ink-subtle focus:outline-none"
            />
          </div>
        ) : (
          <input
            {...rest}
            ref={ref}
            id={fieldId}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            required={required}
            className={controlClasses(invalid)}
          />
        )
      }
    </Field>
  );
});

type NativeTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id' | 'aria-describedby' | 'aria-invalid' | 'className' | 'required'
>;

export interface TextareaProps extends NativeTextareaProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  id?: string;
  className?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required = false, id, className, rows = 3, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      {...(id ? { id } : {})}
      {...(className ? { className } : {})}
    >
      {({ id: fieldId, describedBy, invalid }) => (
        <textarea
          {...rest}
          ref={ref}
          id={fieldId}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          className={controlClasses(invalid, 'resize-y')}
        />
      )}
    </Field>
  );
});
